import { AdminPageHeader, Form, AdminFormAction, DeleteModal } from '@ui';
import { useNavigate } from 'react-router-dom';
import { useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { AuthContext } from '@lp/core/auth';
import { BannedDriversForm } from '@lp/features/admin/forms/BannedDriversForm';
import {
  BANNED_DRIVERS_DEFAULT_VALUES,
  bannedDriversFormSchema,
} from '@lp/features/admin/forms/BannedDriversForm/config';
import { useLoaderData } from '@/hooks/useLoaderData';
import { getChangedValues } from '@/utils/helpers';
import { retrieveUnifiedError } from '@/api/utils';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { AdminPermissions } from '@/permissions';
import { Driver, DriverFormType } from '@/api/apis/resources';
import { useRemoveBannedDriverMutation, useUpdateBannedDriverMutation } from '@/queries/banned-drivers';

export const BannedDriversEdit = () => {
  const navigate = useNavigate();
  const data = useLoaderData<Driver>();
  const { mutateAsync } = useUpdateBannedDriverMutation();
  const { checkPermission } = useContext(AuthContext);

  const hasCreatePermissions = checkPermission(AdminPermissions.ProvidersConfigurationCreate);
  const hasEditPermissions = checkPermission(AdminPermissions.ProvidersConfigurationUpdate);

  const defaultValues = useMemo<DriverFormType>(() => {
    return {
      ...BANNED_DRIVERS_DEFAULT_VALUES,
      firstName: data.firstName,
      lastName: data.lastName,
      affiliationType: data.affiliationType || null,
      txCenterProviderId: data.txCenterProviderId || '',
      txCenterProviderName: data.txCenterProviderId ? data.provider?.name : '',
      companyName: data.companyName || '',
      phone: data.phone || '',
      comment: data.comment || '',
      isBanned: !!data.isBanned,
    };
  }, [data]);

  const handleBackToList = () => {
    navigate('/admin/configuration/banned_drivers');
  };

  const handleSubmit = async (values: DriverFormType) => {
    try {
      const updateBody = {
        firstName: values.firstName,
        lastName: values.lastName,
        affiliationType: values.affiliationType || null,
        txCenterProviderId: values.txCenterProviderId || null,
        companyName: values.companyName || null,
        phone: values.phone || '',
        comment: values.comment || '',
        isBanned: true,
      };

      const changedValues = getChangedValues<Partial<DriverFormType>>(updateBody, defaultValues);

      if (Object.keys(changedValues).length) {
        await mutateAsync({
          id: data.id,
          ...changedValues,
        });
      }

      toast.success(`Banned Driver updated successfully.`);

      handleBackToList();
    } catch (e) {
      const error = retrieveUnifiedError(e);
      toast.error(error.message);
    }
  };

  const { isOpen, onModalOpen, onModalClose, onEntityDelete } = useEntityDelete({
    query: useRemoveBannedDriverMutation,
    successMessage: 'Banned Driver deleted successfully.',
    onDeleteCb: handleBackToList,
  });

  const handleDriverDelete = () => {
    onModalOpen(data.id);
  };

  return (
    <>
      <Form<DriverFormType> defaultValues={defaultValues} schema={bannedDriversFormSchema} onSubmit={handleSubmit}>
        <div className="flex flex-col gap-9">
          <AdminPageHeader title="Edit Banned Driver" backToListLink="/admin/configuration/banned_drivers">
            <AdminFormAction
              type="save"
              showDeleteButton
              handleClick={handleBackToList}
              handleDeleteClick={handleDriverDelete}
              hasDeletePermissions={hasCreatePermissions}
              hasEditPermissions={hasCreatePermissions || hasEditPermissions}
            />
          </AdminPageHeader>
          <BannedDriversForm isReadOnly={!hasCreatePermissions && !hasEditPermissions} />
        </div>
      </Form>
      <DeleteModal
        title="Delete Banned Driver?"
        description={`Are you sure you want to delete ${data.firstName} ${data.lastName} from the banned drivers list?`}
        highlightedText={`${data.firstName} ${data.lastName}`}
        isOpen={isOpen}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
